import { PackageType } from "catchment-common/entities/contractorPackage";
import { packageTypeToString } from "catchment-common/interfaces/contractorPackage";
import { ProjectDetailLineItem } from "catchment-common/interfaces/project";

/**
 * The two-letter tile a scope wears: the first letter of its first two words.
 * "Burleigh Heads Airport" → "BH", "Multiplex" → "M". Never empty — a nameless
 * scope still needs a tile to sit in the rail.
 */
export const initialsOf = (name: string): string => {
  const initials = name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();
  return initials || "–";
};

/** "Gold Coast, QLD" — or whatever parts the address actually has. */
export const projectLocationLabel = (
  project: Pick<ProjectDetailLineItem, "projectAddress">
): string =>
  [project.projectAddress?.city, project.projectAddress?.state]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(", ");

/** One project per (project, package) pair — a user can hold several packages
 * on the same project, and each is a separate place to switch to. */
export const projectKey = (
  project: Pick<ProjectDetailLineItem, "id" | "contractorPackageId">
): string => `${project.id}_${project.contractorPackageId}`;

/**
 * Names that appear more than once in the list. Those rows get a
 * disambiguating suffix — see {@link projectDisplayName}.
 */
export const duplicateProjectNames = (
  projects: ProjectDetailLineItem[]
): Set<string> => {
  const counts = new Map<string, number>();
  for (const project of projects) {
    counts.set(project.projectName, (counts.get(project.projectName) ?? 0) + 1);
  }
  return new Set(
    [...counts.entries()].filter(([, count]) => count > 1).map(([name]) => name)
  );
};

/**
 * The name a project row shows. A duplicate name carries its package (or, for
 * the head contract, its organisation) in brackets so two packages on one
 * project can be told apart; an archived project says so.
 */
export const projectDisplayName = (
  project: ProjectDetailLineItem,
  duplicates: Set<string>
): string => {
  const suffix = duplicates.has(project.projectName)
    ? project.packageType !== PackageType.HeadContractorPackage
      ? (project.packageDescription ?? packageTypeToString(project.packageType))
      : project.organisationName
    : "";

  return `${project.projectName}${suffix ? ` (${suffix})` : ""}${
    project.archived ? " (Archived)" : ""
  }`;
};

/** Case-insensitive, by name — the order the old dropdown used. */
export const sortProjectsByName = (
  projects: ProjectDetailLineItem[]
): ProjectDetailLineItem[] =>
  [...projects].sort((a, b) =>
    a.projectName.localeCompare(b.projectName, undefined, {
      sensitivity: "base"
    })
  );
